import { renderMessage, type RenderOptions } from "./render";

const StreamingCursor = defineComponent(() => {
  return () => (
    <span
      class="inline-block w-[2px] h-[1em] bg-primary-500 rounded-full align-middle ml-0.5 animate-[cursorBlink_1s_steps(2)_infinite]"
    />
  );
});

// While the assistant is still emitting a fenced ```chart or ```card block,
// the closing fence hasn't arrived yet. Depending on how showdown chose to
// handle the unclosed fence, the content may arrive as raw backticks + JSON
// in a <p> tag, or with the fence already consumed. Detect partial rich-block
// JSON by looking for the "__chart":true / "__card":true marker; if it's not
// already safely wrapped in a closed <code> block, swap the in-flight JSON
// out for a skeleton placeholder.
const replaceUnclosedRichBlock = (text: string): string => {
  const chartIdx = text.indexOf('"__chart":true');
  const cardIdx = text.indexOf('"__card":true');
  const markerIdx = chartIdx >= 0 ? chartIdx : cardIdx;
  if (markerIdx < 0) return text;
  const kind = chartIdx >= 0 ? "chart" : "card";

  // If the marker sits inside a fully-closed <code>…</code> run, the real
  // renderer will handle it — leave the text alone.
  const before = text.slice(0, markerIdx);
  const lastCodeOpen = before.lastIndexOf("<code");
  const lastCodeClose = before.lastIndexOf("</code>");
  if (lastCodeOpen > lastCodeClose) {
    const tail = text.slice(markerIdx);
    if (tail.includes("</code>")) return text;
  }

  // If a closing ``` fence exists after the marker, the block is complete —
  // showdown will have (or will soon have) wrapped it in <code>.
  if (text.slice(markerIdx).includes("```")) return text;

  // Otherwise: partial. Cut from the earliest meaningful boundary — the
  // opening ``` if present, otherwise the JSON's opening brace.
  const openFence = before.lastIndexOf("```");
  const openBrace = before.lastIndexOf("{");
  const replaceFrom =
    openFence >= 0 ? openFence : openBrace >= 0 ? openBrace : markerIdx;

  return text.slice(0, replaceFrom) + `<div data-ai-skeleton="${kind}"></div>`;
};

export const Message = defineComponent<{
  text: MaybeRef<string>;
  stream?: boolean;
  options: RenderOptions;
}>((props) => {
  const text = computed(() => replaceUnclosedRichBlock(unref(props.text)));

  return () => {
    const showCursor = props.stream && !text.value.includes("data-ai-skeleton");
    return <div class="ai-chat-message w-full h-auto space-y-3 antialiased">
      {
        renderMessage(text.value, props.options)
      }
      {showCursor && <StreamingCursor />}
    </div>
  }
}, { props: ['text', 'stream', 'options'] })
